import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import EmbeddedDocStyles from "./EmbeddedDocument.module.scss";
import CommentPanel from "../CommentPanel/CommentPanel";
import CommentIcon from "../../assets/images/commentIcon.svg";
import { breadCrumbData, ROUTES } from "../../common/constants";
import { useAppSelector, useAppDispatch } from "../../hooks/store";

// Import styles
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import {
  getDocumentCommentFromServer,
  getFileDataUsingDocId,
} from "../../services/FileUpload.service";
import { revalidateReviewDocument } from "../../services/agent.service";
import Loader from "../Loader/Loader";
import {
  getCommentsWithIdService,
  getSignedUrlUsingDocId,
} from "../../services/common.service";
import { markDocumentAsCompleted } from "../../services/contracts.service";
import { TableTypes } from "../../modules/ManageContract/constants";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import { getToast } from "../../common/utils";
import { useBreadCrumbHook } from "../../hooks/common";
import { reviewDocumentThunk } from "../../store/thunks/askOrion.thunk";
import { ASK_ORION_ACTIONS } from "../../store/slices/askOrion.slice";

interface EmbeddedDocumentProps {
  onOpenDrawer?: (open: boolean) => void;
  // Props for sourcing hub review flow
  useSourcingHubFlow?: boolean;
  docIdFromWorkbench?: string;
  documentLinkFromState?: string;
  showBreadcrumb?: boolean;
}

const EmbeddedDocument = ({
  onOpenDrawer,
  useSourcingHubFlow = false,
  docIdFromWorkbench,
  documentLinkFromState,
  showBreadcrumb = true,
}: EmbeddedDocumentProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(true);
  const [checkingTokenValidity, setCheckingTokenValidity] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [searchParams] = useSearchParams();
  const reviewed = searchParams.get("reviewed") || "false";
  const type = searchParams.get("type") || "";
  const hideCommentSection = [
    TableTypes.BULK_SUMMARY,
    TableTypes.DRAFTING_DASHBOARD,
    TableTypes.RFX_GENERATOR_DASHBOARD,
  ].includes(type);
  
  // Add ref to track if API has been called for review_inprogress
  const hasCalledReviewAPI = useRef(false);
  
  // For sourcing hub flow, get docFileId from props, otherwise from URL params
  const urlDocId = searchParams.get("url") || "";
  const [docFileId, setDocFileId] = useState(
    useSourcingHubFlow && docIdFromWorkbench ? docIdFromWorkbench : urlDocId
  );
  
  const [loading, setLoading] = useState(false);
  const [fileData, setFileData] = useState({} as any);
  const [disco, setDisco] = useState<any>();
  const [commentsWithId, setCommentsWithId] = useState();
  const [isContractReviewCompleted, setIsContractReviewCompleted] = useState(
    fileData?.doc_details?.status === "review_completed"
  );
  const location = useLocation();

  const { docId: contractsReviewDocId } = useAppSelector(
    (store) => store?.rootReducer?.vscDashboardReducer?.contractsReview
  ) as {
    docId?: string;
  };

  // Get sourcing hub review state from Redux
  const reviewLoading = useAppSelector((store) => store?.rootReducer?.askOrionReducer?.reviewLoading);
  const reviewResults = useAppSelector((store) => store?.rootReducer?.askOrionReducer?.reviewResults);
  const reviewError = useAppSelector((store) => store?.rootReducer?.askOrionReducer?.reviewError);
  const documentName = useAppSelector((store) => store?.rootReducer?.askOrionReducer?.documentName);
  const totalIssuesFound = useAppSelector((store) => store?.rootReducer?.askOrionReducer?.totalIssuesFound);
  const reviewedFileDriveUrl = useAppSelector((store) => store?.rootReducer?.askOrionReducer?.reviewedFileDriveUrl);
  const reviewedFileId = useAppSelector((store) => store?.rootReducer?.askOrionReducer?.reviewedFileId);
  const reviewSummary = useAppSelector((store) => store?.rootReducer?.askOrionReducer?.reviewSummary);
  const reviewComments = useAppSelector((store) => store?.rootReducer?.askOrionReducer?.reviewComments);
  const googleDocMeta = useAppSelector((store) => store?.rootReducer?.askOrionReducer?.googleDocMeta);

 
  useEffect(() => {
    if (location.state?.keepDrawerOpen) {
      if (onOpenDrawer) {
        onOpenDrawer(true); // this triggers toggleDrawer(true) in App
      }
    }
  }, [location.state?.keepDrawerOpen]);

  const [pdfUrl, setPdfUrl] = useState("");
  const [showMoveToCommentLoader, setShowMoveToCommentLoader] = useState(false);

  const getCommentsWithId = async (docFileId: any, feature?: string) => {
    try {
      const res = await getCommentsWithIdService(docFileId, feature);
      setCommentsWithId(res?.data);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.status_message;
      getToast("Error", errorMessage || "Error while fetching comment id");
    }
  };

  // Validate Again handler for sourcing hub flow
  const handleValidateAgain = async () => {
    if (!useSourcingHubFlow || !docFileId) return;
    try {
      // Show loader on comment panel AND iframe area
      setLoading(true);
      setShowLoader(true);
      
      // Call revalidate endpoint
      const revalidateResponse = await revalidateReviewDocument({ doc_id: docFileId });
      
      // Extract reviewed_file_id from nested response structure: doc_details.drive_details.reviewed_file_id
      const reviewedFileIdFromResponse = revalidateResponse?.doc_details?.drive_details?.reviewed_file_id;
      
      if (reviewedFileIdFromResponse) {
        // Refresh google doc meta using reviewed_file_id so comments re-render
        const res = await getCommentsWithIdService(reviewedFileIdFromResponse);
        setCommentsWithId(res?.data);
        
        // Update Redux state with new reviewedFileId so iframe reloads with the new document
        dispatch({
          type: 'ASK_ORION/REVIEW_DOCUMENT_SUCCESS',
          payload: revalidateResponse,
        });
        
        // Increment iframe key to force reload with new reviewed_file_id
        setIframeKey((k) => k + 1);
        // Note: setShowLoader(false) will be called in iframe's onLoad event
      } else {
        getToast("Error", "Reviewed file ID not found in response");
        setShowLoader(false);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.status_message;
      getToast("Error", errorMessage || "Error during validation");
      setShowLoader(false);
    } finally {
      setLoading(false);
    }
  };

  const orignalGoogleDocId =
    fileData?.doc_details?.drive_details?.original_file_id;
  const reviewGoogleDocId =
    fileData?.doc_details?.drive_details?.reviewed_file_id;

  const [showLoader, setShowLoader] = useState(false);
  const [processingStage, setProcessingStage] = useState(0);
  const handleShowComments = (state: boolean): any => {
    setShowComments(state);
  };
  const handleStartReview = async () => {
    if (useSourcingHubFlow && docIdFromWorkbench) {
      // Use sourcing hub review flow
      console.log("=== handleStartReview called (sourcing hub) ===");
      console.log("tab param:", searchParams.get('tab'));
      setLoading(true);
      setProcessingStage(1);
      try {
        // Get tab parameter from URL
        const tab = searchParams.get('tab') || undefined;
        console.log("Calling reviewDocumentThunk with doc_id:", docIdFromWorkbench, "tab:", tab);
        await dispatch(reviewDocumentThunk({ doc_id: docIdFromWorkbench, tab }));
        console.log("reviewDocumentThunk completed successfully");
        setLoading(false);
      } catch (error: any) {
        console.error("reviewDocumentThunk error:", error);
        setLoading(false);
        setProcessingStage(0);
      }
    } else {
      // Use original contract review flow
      await getDocumentComments();
    }
  };
  const incrementIframeKey = () => {
    setIframeKey(iframeKey + 1);
  };

  const handleMtcLoader = () => {
    setShowMoveToCommentLoader(true);
    setTimeout(() => {
      setShowMoveToCommentLoader(false);
    }, 4000);
  };

  useEffect(() => {
    if (fileData?.doc_details?.drive_details?.reviewed_file_id) {
      setProcessingStage(
        !fileData?.doc_details?.drive_details?.reviewed_file_id ? 0 : 1
      );
    }
    if (fileData?.doc_details?.status) {
      setIsContractReviewCompleted(
        fileData?.doc_details?.status === "review_completed"
      );
    }
    
    // Only call getCommentsWithId for non-sourcing hub flow
    // For sourcing hub flow, googleDocMeta comes from Redux (already called in thunk)
    if (fileData?.doc_details?.drive_details?.reviewed_file_id && !useSourcingHubFlow) {
      getCommentsWithId(fileData?.doc_details?.drive_details?.reviewed_file_id);
    }
  }, [fileData, useSourcingHubFlow]);

  // Handle sourcing hub review loading state
  useEffect(() => {
    if (useSourcingHubFlow && docIdFromWorkbench) {
      setLoading(reviewLoading);
    }
  }, [reviewLoading, useSourcingHubFlow, docIdFromWorkbench]);

  // Handle sourcing hub review error
  useEffect(() => {
    if (reviewError && useSourcingHubFlow && docIdFromWorkbench) {
      getToast("Error", reviewError || "Error during document review");
      setProcessingStage(0);
    }
  }, [reviewError, useSourcingHubFlow, docIdFromWorkbench]);

  // Reload iframe when reviewed file is available (sourcing hub flow)
  useEffect(() => {
    if (reviewedFileDriveUrl && useSourcingHubFlow && docIdFromWorkbench) {
      setIframeKey(iframeKey + 1);
    }
  }, [reviewedFileDriveUrl, useSourcingHubFlow, docIdFromWorkbench]);

  // Set commentsWithId from googleDocMeta (sourcing hub flow)
  useEffect(() => {
    if (googleDocMeta && useSourcingHubFlow && docIdFromWorkbench) {
      console.log("=== Setting commentsWithId from googleDocMeta ===");
      console.log("googleDocMeta:", googleDocMeta);
      
      // Handle both possible response structures: { data: [...] } or [...]
      const metaData = Array.isArray(googleDocMeta) ? googleDocMeta : googleDocMeta?.data;
      console.log("Extracted metaData:", metaData);
      
      setCommentsWithId(metaData);
    }
  }, [googleDocMeta, useSourcingHubFlow, docIdFromWorkbench]);

  // Handle sourcing hub review results
  useEffect(() => {
    if (useSourcingHubFlow && reviewComments && Object.keys(reviewComments).length > 0 && docIdFromWorkbench) {
      console.log("=== Processing review results ===");
      // Calculate severity counts from review_summary
      let highCount = 0;
      let mediumCount = 0;
      let lowCount = 0;
      
      if (reviewSummary && reviewSummary.length > 0) {
        // Use for...of instead of forEach to avoid mutation warnings
        for (const item of reviewSummary) {
          const categoryComments = reviewComments[item.category] || [];
          for (const comment of categoryComments) {
            if (comment.severity === "high") highCount++;
            else if (comment.severity === "medium") mediumCount++;
            else if (comment.severity === "low") lowCount++;
          }
        }
      }
      
      // Deep clone reviewComments and reviewSummary to prevent mutation
      const clonedReviewComments = Object.keys(reviewComments).reduce((acc, category) => {
        acc[category] = reviewComments[category].map((comment: any) => ({ ...comment }));
        return acc;
      }, {} as any);
      
      const clonedReviewSummary = reviewSummary.map((item: any) => ({ ...item }));
      
      const transformedData = {
        doc_details: {
          display_name: documentName || "Document",
          status: "review_completed",
          count_of_high_severity: highCount,
          count_of_medium_severity: mediumCount,
          count_of_low_severity: lowCount,
        },
        review_comments: clonedReviewComments,
        review_summary: clonedReviewSummary,
      };
      console.log("Setting fileData with transformed data");
      setFileData(transformedData);
      setProcessingStage(1);
      setIsContractReviewCompleted(true);
    }
  }, [reviewComments, reviewSummary, useSourcingHubFlow, docIdFromWorkbench, documentName]);

  useEffect(() => {
    if (useSourcingHubFlow && docIdFromWorkbench) {
      // Reset review state and API call tracker when navigating to a new document
      console.log("=== Resetting review state for new document ===");
      console.log("docIdFromWorkbench:", docIdFromWorkbench);
      dispatch({ type: ASK_ORION_ACTIONS.RESET_REVIEW_STATE });
      hasCalledReviewAPI.current = false; // Reset the API call tracker
      
      // For sourcing hub flow, don't fetch file data on mount
      // Data will come from Redux after review
      setShowLoader(false);
      setProcessingStage(0);
      
      // If tab is review_inprogress or review_completed, call the API immediately on mount (only once)
      const tab = searchParams.get('tab');
      console.log("=== Initial mount useEffect ===");
      console.log("tab:", tab, "hasCalledReviewAPI:", hasCalledReviewAPI.current);
      
      if ((tab === 'review_inprogress' || tab === 'review_completed') && !hasCalledReviewAPI.current) {
        console.log("Auto-calling handleStartReview for", tab, "tab");
        hasCalledReviewAPI.current = true; // Mark as called
        handleStartReview();
      }
    } else if (docFileId) {
      if (type === TableTypes.BULK_SUMMARY) {
        getPdfUrlUsingDocId(docFileId);
      } else if (
        type === TableTypes.DRAFTING_DASHBOARD ||
        type === TableTypes.RFX_GENERATOR_DASHBOARD
      ) {
        setPdfUrl(docFileId);
      } else {
        getAllDataUsingDocId(docFileId);
      }
    }
  }, [docFileId, type, useSourcingHubFlow, docIdFromWorkbench]);

  // const handleCheckTokenValidity = async () => {
  //   await checkTokenValidityAndUpdateIfExpired();
  // };

  const handleMoveToComment = async (commentId: string) => {
    console.log("=== handleMoveToComment called ===");
    console.log("commentId:", commentId);
    console.log("useSourcingHubFlow:", useSourcingHubFlow);
    console.log("googleDocMeta:", googleDocMeta);
    console.log("reviewedFileId:", reviewedFileId);
    console.log("processingStage:", processingStage);
    
    // For sourcing hub flow, check if review has been completed (processingStage === 1)
    if (useSourcingHubFlow && googleDocMeta && reviewedFileId && processingStage === 1) {
      // For sourcing hub flow, find matching comment from googleDocMeta
      console.log("=== Sourcing Hub Flow - Comment Click ===");
      console.log("Looking for commentId:", commentId);
      
      // Handle both possible response structures: { data: [...] } or [...]
      const metaData = Array.isArray(googleDocMeta) ? googleDocMeta : googleDocMeta?.data;
      
      if (!metaData || !Array.isArray(metaData)) {
        console.error("googleDocMeta is not in expected format:", googleDocMeta);
        return;
      }
      
      console.log("Meta data array:", metaData);
      
      const matchingComment = metaData.find((metaComment: any) => {
        // Try to match by content (trimmed and case-insensitive)
        const contentMatch = metaComment.content?.trim().toLowerCase() === commentId?.trim().toLowerCase();
        const idMatch = metaComment.comment_id === commentId;
        
        console.log("Checking meta comment:", {
          content: metaComment.content,
          comment_id: metaComment.comment_id,
          contentMatch,
          idMatch
        });
        
        return contentMatch || idMatch;
      });
      
      console.log("Matching comment found:", matchingComment);
      
      if (matchingComment?.comment_id) {
        console.log("✅ Setting disco to comment_id:", matchingComment.comment_id);
        setDisco(matchingComment.comment_id);
        incrementIframeKey();
      } else {
        console.warn("No matching comment found for:", commentId);
        console.warn("Available comments in googleDocMeta:", metaData.map((c: any) => ({
          content: c.content,
          comment_id: c.comment_id
        })));
      }
    } else {
      // For original contract review flow, use commentId directly
      console.log("=== Original Flow - Comment Click ===");
      console.log("❌ USING ELSE BLOCK - Setting disco to commentId (description):", commentId);
      console.log("Condition failed - useSourcingHubFlow:", useSourcingHubFlow, "googleDocMeta:", !!googleDocMeta, "reviewedFileId:", !!reviewedFileId, "processingStage:", processingStage);
      setDisco(commentId);
      incrementIframeKey();
    }
  };

  const handleMarkDocumentComplete = async (docId: string) => {
    const res = await markDocumentAsCompleted(docId, useSourcingHubFlow);
    if (res) {
      setIsContractReviewCompleted(true);
      
      // For sourcing hub flow, redirect to workbench dashboard after successful completion
      if (useSourcingHubFlow) {
        navigate('/workbench-dashboard');
      } else {
        // For original contract review flow, navigate back to Manage Contracts and show the Completed tab
        navigate(`${ROUTES.manageContracts}?tab=${TableTypes.REVEIW_COMPLETED}`);
      }
    }
  };

  const getAllDataUsingDocId = async (doc_id: string) => {
    setShowLoader(true);
    try {
      if(doc_id){
      const response = await getFileDataUsingDocId(doc_id);
      setFileData(response);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.status_message;
      getToast("Error", errorMessage || "Cannot fetch file");
    } finally {
      setShowLoader(false);
    }

    // setComments(response?.review_comments)
  };

  const getPdfUrlUsingDocId = async (doc_id: string) => {
    setShowLoader(true);
    const res = await getSignedUrlUsingDocId(doc_id);
    setPdfUrl(res?.signed_url);
  };

  useEffect(() => {
    if (useSourcingHubFlow && docIdFromWorkbench) {
      // For sourcing hub flow, set docFileId from props
      setDocFileId(docIdFromWorkbench);
    } else if (contractsReviewDocId) {
      setDocFileId(contractsReviewDocId);
    } else {
      setDocFileId(searchParams.get("url") || "");
    }
  }, [searchParams, contractsReviewDocId, useSourcingHubFlow, docIdFromWorkbench]);

  const renderForReviewedDocs = useCallback(async () => {
    setProcessingStage(1);
  }, []);

  useEffect(() => {
    if (reviewed === "true") {
      renderForReviewedDocs();
    }
  }, [reviewed, renderForReviewedDocs]);

  const getDocumentComments = async () => {
    if (fileData?.doc_details?.status === "review_pending") {
      try {
        setProcessingStage(1);
        setLoading(true);
        await getDocumentCommentFromServer({
          doc_id: docFileId,
        });
        await getAllDataUsingDocId(docFileId);
        setLoading(false);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.status_message;
        getToast("Error", errorMessage || "Some error occured");
        setProcessingStage(0);
      }
    }
  };

  const backUrl = searchParams.get("backurl") || "";

  const computedBreadcrumb = useMemo(() => {
    try {
      const backUrlObj = new URL(`http://dummy.com${backUrl}`);
      const pathname = backUrlObj.pathname;
      const backParams = new URLSearchParams(backUrlObj.search);
      const cardType = backParams.get("cardType") || "";
      const nestedType = backParams.get("type") || "";

      // Mapping cardType with breadCrumbData from constants
      const cardTypeMap: Record<string, keyof typeof breadCrumbData> = {
        contract_drafting: "contractDraftingDashboard",
        rfx_generator: "rfxGeneratorDashboard",
        contract_redlining: "contractRedliningDashboard",
        clause_check: "keyContractClausesDashboard",
      };

      if (pathname === "/dashboard" && cardTypeMap[cardType]) {
        return [breadCrumbData[cardTypeMap[cardType]]];
      }

      if (pathname === ROUTES.manageContracts) {
        return [
          {
            ...breadCrumbData.manageContracts,
            link: `${breadCrumbData.manageContracts.link}?&url=${nestedType}`,
          },
        ];
      }

      if (pathname === ROUTES.clauseSummary) {
        return [breadCrumbData.clauseSummary];
      }

      return [breadCrumbData.dashboard];
    } catch {
      return [breadCrumbData.dashboard];
    }
  }, [backUrl]);

  useBreadCrumbHook(computedBreadcrumb);

  // Determine which document ID to use for iframe
  let docIdForIframe = "";
  console.log("=== Determining docIdForIframe ===");
  console.log("useSourcingHubFlow:", useSourcingHubFlow);
  console.log("reviewedFileId:", reviewedFileId);
  console.log("documentLinkFromState:", documentLinkFromState);
  console.log("docIdFromWorkbench:", docIdFromWorkbench);
  console.log("tab param:", searchParams.get('tab'));
  console.log("processingStage:", processingStage);
  
  const tab = searchParams.get('tab');
  
  if (useSourcingHubFlow && reviewedFileId && processingStage === 1) {
    // Use reviewedFileId for all tabs after review has been completed (processingStage === 1)
    // This includes review_not_started after clicking "Start Review"
    docIdForIframe = reviewedFileId;
    console.log("Using reviewedFileId for iframe (after review completed):", reviewedFileId);
  } else if (useSourcingHubFlow && documentLinkFromState) {
    // For sourcing hub flow before review, use documentLinkFromState (current row's document)
    // Extract doc ID from documentLinkFromState
    const match = documentLinkFromState.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (match) {
      docIdForIframe = match[1];
      console.log("Extracted docId from documentLinkFromState:", docIdForIframe);
    }
  } else {
    // For original flow, use reviewGoogleDocId or orignalGoogleDocId
    docIdForIframe = reviewGoogleDocId || orignalGoogleDocId || "";
    console.log("Using original flow docId:", docIdForIframe);
  }
  
  console.log("Final docIdForIframe:", docIdForIframe);
  
  // Log iframe URL construction
  if (useSourcingHubFlow && docIdForIframe) {
    const iframeUrl = `https://docs.google.com/document/d/${docIdForIframe}/edit${disco ? `?disco=${disco}` : ""}`;
    console.log("=== Iframe URL (Sourcing Hub) ===");
    console.log("docIdForIframe:", docIdForIframe);
    console.log("disco:", disco);
    console.log("Full URL:", iframeUrl);
  }

  return (
    <div className={EmbeddedDocStyles.container}>
      {showBreadcrumb && <Breadcrumb list={computedBreadcrumb} />}
      <div className={EmbeddedDocStyles["doc-wrapper"]}>
        <>
          {" "}
          <div
            style={{
              width: "auto",
              flexGrow: "1",
              position: "relative",
              height: "100%",
              overflow: `${showLoader ? "hidden" : "scroll"}`,
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%,-50%)",
                // zIndex: -1,
              }}
            >
              {showLoader && (
                <Loader
                // loaderText={""}
                />
              )}
            </div>

            <div
              className={`${EmbeddedDocStyles.mtcLoader} ${
                showMoveToCommentLoader ? `${EmbeddedDocStyles.active}` : ""
              }`}
            >
              {" "}
              <Loader loaderText={"Moving to comment"} />
            </div>

            {(orignalGoogleDocId || reviewGoogleDocId || pdfUrl || docIdForIframe) && (
              <iframe
                key={iframeKey}
                height={"100%"}
                width={"100%"}
                title="Google Doc"
                onLoad={() => {
                  console.log("=== iframe onLoad event ===");
                  setShowLoader(false);
                  setShowMoveToCommentLoader(false);
                }}
                src={
                  useSourcingHubFlow && docIdForIframe
                    ? `https://docs.google.com/document/d/${docIdForIframe}/edit${disco ? `?disco=${disco}` : ""}`
                    : type === TableTypes.BULK_SUMMARY
                    ? pdfUrl
                    : type === TableTypes.DRAFTING_DASHBOARD ||
                      type === TableTypes.RFX_GENERATOR_DASHBOARD
                    ? `https://docs.google.com/document/d/${pdfUrl}/edit`
                    : `https://docs.google.com/document/d/${
                        reviewGoogleDocId
                          ? reviewGoogleDocId
                          : orignalGoogleDocId
                      }/edit${disco ? `?disco=${disco}` : ""}`
                }
              ></iframe>
            )}
          </div>
          {!hideCommentSection ? (
            <>
              {" "}
              {showComments && (
                <CommentPanel
                  comments={fileData?.review_comments}
                  handleMoveToComment={handleMoveToComment}
                  suggestions={fileData?.review_summary}
                  handleShowComments={handleShowComments}
                  processingStage={processingStage}
                  handleStartReview={handleStartReview}
                  loading={loading}
                  fileName={fileData?.doc_details?.display_name}
                  commentsWithId={commentsWithId}
                  isContractReviewCompleted={isContractReviewCompleted}
                  fileId={docFileId}
                  useSourcingHubFlow={useSourcingHubFlow}
                  handleMarkDocumentComplete={handleMarkDocumentComplete}
                  handleMtcLoader={handleMtcLoader}
                  docDetails={fileData?.doc_details}
                  googleDocMeta={useSourcingHubFlow ? googleDocMeta : undefined}
                  handleValidateAgain={handleValidateAgain}
                />
              )}
              {!showComments && (
                <div
                  className={EmbeddedDocStyles.showComments}
                  onClick={() => setShowComments(true)}
                >
                  <img src={CommentIcon} alt="" />
                </div>
              )}{" "}
            </>
          ) : (
            <></>
          )}
        </>
      </div>
    </div>
  );
};

export default EmbeddedDocument;
